import React, { useEffect, useMemo, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Check, ChevronDownIcon, ChevronsUpDown, Webcam } from "lucide-react";
import { Controller, FieldErrors, FormProvider, useForm } from "react-hook-form";
import {
  BranchTagConfig,
  DailySchedule,
  DepartmentTagConfig,
  MetadataByType,
  ServiceTagConfig,
  TagCreatePayload,
  TagMetadataConfig,
  TagType,
  TagUpdateRequest,
  WeekDay,
} from "@/app/types/tags";
import { Button } from "@/components/ui/Button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/Command";
import { ConfirmationDialog } from "@/components/ui/common/ConfirmationDailog";
import PhoneInputField from "@/components/custom-component/PhoneInputField";
import { FloatingLabelInput } from "@/components/ui/floating-lable/Floating";
import { LoadingButton } from "@/components/ui/loading-button/LoadingButton";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/Popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { Switch } from "@/components/ui/Switch";
import TimeInput from "@/components/custom-component/TimePickerInput";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/Sheet";
import { toast } from "@/components/ui/UseToast";
import { TAG_TYPES } from "@/constants/tags";
import { CreateTagRequestSchema } from "@/schema/TagSchema";
import { cn } from "@/lib/utils";
import { serviceDays } from "@/mock/services";
import { useGetStaffListQuery } from "@/services/StaffsApi";
import {
  useCreateTagMutation,
  useGetTagByIdQuery,
  useGetTagDropdownQuery,
  useUpdateTagMutation,
} from "@/services/TagsApi";
import { generateTagCodeFromName } from "@/utils/Helper";
import { UploadCognitoImage } from "@/utils/lib/S3Service";
import { useGetPublishedFormListQuery } from "@/services/FormBuilderApi";
import { default as profileimg } from "@/assets/default.jpg";
import z from "zod";
import { InputTags } from "@/components/ui/input-tags/Tags";
import { useSelector } from "react-redux";
import { RootState } from "@/app/store";

const { VITE_ORG_ID, VITE_VIEW_S3_URL } = import.meta.env;
const ORG_ID = Number(VITE_ORG_ID ?? 1);

type TagFormAction = "add" | "edit";
type StaffOption = { value: string; label: string };

const DEFAULT_BRANCH_METADATA: BranchTagConfig = {
  routing_permission: "within",
  branch_manager_id: null,
};

const DEFAULT_DEPARTMENT_METADATA: DepartmentTagConfig = {
  logo: "",
  re_routing: "across",
  description: "",
  coordinator_id: null,
  contact_number: "",
};

const DEFAULT_SERVICE_METADATA: ServiceTagConfig = {
  lead_id: null,
  country_code: "+92",
  contact_number: "",
  average_processing_time: 10,
  max_tickets: null,
  visibility: "visible",
  logo: "",
  form_id: null,
  days_of_week: [],
  apply_time_to_all_days: false,
  schedules: [],
};

const buildDefaultMetadata = (tagType: TagType): TagMetadataConfig => {
  if (tagType === "branch") return { ...DEFAULT_BRANCH_METADATA };
  if (tagType === "department") return { ...DEFAULT_DEPARTMENT_METADATA };
  return { ...DEFAULT_SERVICE_METADATA };
};

const toWeekDay = (value: string): WeekDay => value.toLowerCase() as WeekDay;

const isValidWeekDay = (value: string): value is WeekDay =>
  [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ].includes(value);

const parseSchedule = (raw: any): DailySchedule | null => {
  const dayRaw = typeof raw?.day === "string" ? raw.day.toLowerCase() : "";
  if (!isValidWeekDay(dayRaw)) return null;
  return {
    day: dayRaw,
    opening_time: String(raw?.opening_time ?? ""),
    break_start_time: raw?.break_start_time ? String(raw.break_start_time) : null,
    break_end_time: raw?.break_end_time ? String(raw.break_end_time) : null,
    ticket_end_time: String(raw?.ticket_end_time ?? ""),
    closing_time: String(raw?.closing_time ?? ""),
  };
};


const initialValues: TagFormValues = {
  org_id: ORG_ID,
  parent_tag_id: null,
  name: "",
  code: "",
  type: "branch",
  tag_status: true,
  event_id: null,
  metadata: DEFAULT_BRANCH_METADATA,
};

const formInitialValues: TagFormValues = { ...initialValues };
type TagFormValues = z.infer<typeof CreateTagRequestSchema>;

const TagsForm = ({
  open,
  setOpen,
  refetch,
  action,
  setAction,
  tagId,
  prefillParentTagId,
  prefillType,
  eventId,
  onClose,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  refetch: () => void;
  action: TagFormAction;
  setAction: React.Dispatch<React.SetStateAction<TagFormAction>>;
  tagId: number | undefined;
  prefillParentTagId?: number;
  prefillType?: TagType;
  eventId?: number;
  onClose?: () => void;
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isParentComboboxOpen, setIsParentComboboxOpen] = useState(false);
  const [isBranchManagerOpen, setIsBranchManagerOpen] = useState(false);
  const [isCoordinatorOpen, setIsCoordinatorOpen] = useState(false);
  const [isLeadOpen, setIsLeadOpen] = useState(false);
  const [applyToAllDays, setApplyToAllDays] = useState(false);
  const [avatar, setAvatar] = useState<string | ArrayBuffer | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [createTag, { isLoading }] = useCreateTagMutation();
  const [updateTag, { isLoading: isUpdating }] = useUpdateTagMutation();
  const { data: staffOptions = [] } = useGetStaffListQuery({ query: "" });
  const { data: formOptions = [] } = useGetPublishedFormListQuery();
  const { userInfo } = useSelector((state: RootState) => state.auth);
  const userOrgId = Number(userInfo?.org_id);
  
  const { data: tagById } = useGetTagByIdQuery(tagId as number, {
    skip: action !== "edit" || !tagId || !open,
  });
  const previousTagTypeRef = useRef<TagType | undefined>(formInitialValues.type);

  
  const form = useForm<TagFormValues>({
    mode: "all",
    defaultValues: { ...formInitialValues, org_id: userOrgId },
    resolver: zodResolver(CreateTagRequestSchema),
  });

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
    clearErrors,
  } = form;

  const watchedName = watch("name");
  const watchedTagType = watch("type");
  const watchedOrgId = watch("org_id");
  const watchedEventId = watch("event_id");
  const watchedParentTagId = watch("parent_tag_id");
  const watchedStatus = watch("tag_status");
  const watchedMetadata = watch("metadata");
  const [selectedDays, setSelectedDays] = useState<number[]>([]);

  const dropdownTagType = useMemo(() => {
    if (watchedTagType === "service") return ["department", "service"];
    if (watchedTagType === "department") return ["branch"];
    return undefined;
  }, [watchedTagType]);

  const { data: parentDropdownOptions = [] } = useGetTagDropdownQuery(
    {
      tag_types: dropdownTagType as TagType[],
      org_id: watchedOrgId,
      event_id: watchedEventId ?? undefined,
    },
    { skip: !dropdownTagType || !watchedOrgId }
  );

  const selectedParent = useMemo(
    () => parentDropdownOptions.find((item) => item.tag_id === watchedParentTagId),
    [parentDropdownOptions, watchedParentTagId]
  );

  const branchMetadata = watchedTagType === "branch"
    ? (watchedMetadata as BranchTagConfig)
    : DEFAULT_BRANCH_METADATA;
  const departmentMetadata = watchedTagType === "department"
    ? (watchedMetadata as DepartmentTagConfig)
    : DEFAULT_DEPARTMENT_METADATA;
  const serviceMetadata = watchedTagType === "service"
    ? (watchedMetadata as ServiceTagConfig)
    : DEFAULT_SERVICE_METADATA;

  const currentLogo =
    watchedTagType === "department"
      ? departmentMetadata.logo
      : watchedTagType === "service"
        ? serviceMetadata.logo
        : "";

  useEffect(() => {
    setValue("code", generateTagCodeFromName(watchedName ?? ""), {
      shouldValidate: true,
    });
  }, [setValue, watchedName]);

  useEffect(() => {
    if (!watchedTagType) return;
    if (previousTagTypeRef.current === watchedTagType) return;

    previousTagTypeRef.current = watchedTagType;
    setValue("metadata", buildDefaultMetadata(watchedTagType), {
      shouldValidate: true,
    });
    setValue("parent_tag_id", watchedTagType === "branch" ? null : undefined);
    setApplyToAllDays(false);
    setSelectedImage(null);
    setAvatar(null);
  }, [setValue, watchedTagType]);

  function createDefaultTag<T extends TagType>(type: T): Extract<TagFormValues, { type: T }> {
    return {
      ...formInitialValues,
      org_id: userOrgId,
      type,
      event_id: eventId ?? formInitialValues.event_id,
      metadata: buildDefaultMetadata(type),
      parent_tag_id:
        type === "branch"
          ? null
          : prefillParentTagId ?? formInitialValues.parent_tag_id,
    } as Extract<TagFormValues, { type: T }>
  }

  useEffect(() => {
    if (!open) return;

    if (action === "edit" && tagById) {
      previousTagTypeRef.current = tagById.type;

      if (tagById.type === "service") {
        setApplyToAllDays(!!tagById.metadata.apply_time_to_all_days);
      }

      reset(tagById);
      return;
    }

    const defaultType = prefillType ?? formInitialValues.type;
    previousTagTypeRef.current = defaultType;
    setApplyToAllDays(false);
    reset(createDefaultTag(defaultType))
  }, [action, open, prefillParentTagId, prefillType, reset, tagById]);

  useEffect(() => {
    if (watchedTagType !== "service") {
      setSelectedDays([]);
      return;
    }

    const mappedDays = serviceMetadata?.days_of_week
      ?.map(
        (day) =>
          serviceDays.find(
            (item) => item.day.toLowerCase() === day.toLowerCase()
          )?.id
      )
      .filter((id): id is number => typeof id === "number");

    setSelectedDays(mappedDays);
  }, [serviceMetadata?.days_of_week, watchedTagType]);

  useEffect(() => {
    if (selectedImage) return;
    if (currentLogo) {
      const logoValue = String(currentLogo);
      const resolvedLogo =
        logoValue.startsWith("http://") ||
        logoValue.startsWith("https://") ||
        logoValue.startsWith("data:")
          ? logoValue
          : `${VITE_VIEW_S3_URL ?? ""}${logoValue}`;
      setAvatar(resolvedLogo);
      return;
    }
    setAvatar(null);
  }, [currentLogo, selectedImage]);

  const closeDialog = () => setIsDialogOpen(false);

  function setMetadataField<
    T extends TagType,
    K extends keyof MetadataByType[T]
  >(
    type: T,
    key: K,
    value: MetadataByType[T][K]
  ) {
    const metadata = form.getValues("metadata") as MetadataByType[T]

    setValue(
      "metadata",
      { ...metadata, [key]: value },
      { shouldValidate: true }
    )
  }


  const setLogoInMetadata = (logoPath: string) => {
    if (watchedTagType === "department") {
      setMetadataField("department", "logo", logoPath);
      return;
    }
    if (watchedTagType === "service") {
      setMetadataField("service", "logo", logoPath);
    }
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validTypes = ["image/png", "image/jpg", "image/jpeg"];
    const maxSizeMB = 1;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    if (!validTypes.includes(file.type)) {
      toast({
        variant: "destructive",
        title: "Error Uploading Image",
        description: "Unsupported image type. Only PNG, JPG, and JPEG are supported.",
      });
      event.target.value = "";
      return;
    }

    if (file.size > maxSizeBytes) {
      toast({
        variant: "destructive",
        title: "Error Uploading Image",
        description: `Image size exceeds ${maxSizeMB} MB limit.`,
      });
      event.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setSelectedImage(file);
      if (e.target?.result !== undefined) {
        setAvatar(e.target.result);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSetAvatarClick = () => {
    document.getElementById("tagMetadataLogoInput")?.click();
  };

  const handleRemoveImageClick = () => {
    setSelectedImage(null);
    setAvatar(null);
    setLogoInMetadata("");
    const fileInput = document.getElementById("tagMetadataLogoInput") as HTMLInputElement | null;
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const deriveCountryCode = (phone: string): string => {
    const value = phone.startsWith("+") ? phone : `+${phone}`;
    const match = value.match(/^\+\d{1,4}/);
    return match ? match[0] : "+92";
  };

  const setServiceContactNumber = (value: string) => {
    setMetadataField("service", "contact_number", value);
    setMetadataField("service", "country_code", deriveCountryCode(value));
  };

  const updateServiceSchedule = (
    updater: (schedule: DailySchedule[]) => DailySchedule[]
  ) => {
    const nextSchedule = updater(serviceMetadata.schedules);
    const nextDays = Array.from(
      new Set(nextSchedule.map((slot) => slot.day))
    ).sort(
      (a, b) =>
        serviceDays.findIndex((item) => item.day.toLowerCase() === a) -
        serviceDays.findIndex((item) => item.day.toLowerCase() === b)
    ) as WeekDay[];

    setValue(
      "metadata",
      {
        ...serviceMetadata,
        schedules: nextSchedule,
        days_of_week: nextDays,
      },
      { shouldValidate: true }
    );
  };

  const toggleDaySelection = (dayId: number, dayName: string) => {
    const dayKey = toWeekDay(dayName);
    const isSelected = selectedDays.includes(dayId);
    const nextDays = isSelected
      ? selectedDays.filter((id) => id !== dayId)
      : [...selectedDays, dayId];

    const sortedDays = nextDays.sort((a, b) => {
      const indexA = serviceDays.findIndex((day) => day.id === a);
      const indexB = serviceDays.findIndex((day) => day.id === b);
      return indexA - indexB;
    });
    setSelectedDays(sortedDays);

    updateServiceSchedule((currentSchedule) => {
      if (isSelected) {
        return currentSchedule.filter((slot) => slot.day !== dayKey);
      }

      return [
        ...currentSchedule,
        {
          day: dayKey,
          opening_time: "",
          break_start_time: null,
          break_end_time: null,
          ticket_end_time: "",
          closing_time: "",
        },
      ];
    });
  };

  const handleServiceScheduleTimeChange = (
    dayName: string,
    field:
      | "opening_time"
      | "break_start_time"
      | "break_end_time"
      | "ticket_end_time"
      | "closing_time",
    value: string
  ) => {
    const targetDay = toWeekDay(dayName);

    updateServiceSchedule((currentSchedule) => {
      if (!applyToAllDays) {
        return currentSchedule.map((slot) =>
          slot.day === targetDay
            ? { ...slot, [field]: value || null }
            : slot
        );
      }

      const selectedDayNames = selectedDays
        .map((id) => serviceDays.find((day) => day.id === id)?.day)
        .filter((day): day is string => Boolean(day))
        .map((day) => toWeekDay(day));

      return currentSchedule.map((slot) =>
        selectedDayNames.includes(slot.day)
          ? { ...slot, [field]: value || null }
          : slot
      );
    });
  };

  const handleClose = () => {
    clearErrors();
    previousTagTypeRef.current = formInitialValues.type;
    reset({ ...formInitialValues, org_id: userOrgId });
    setAction("add");
    onClose?.();
    setOpen(false);
    setApplyToAllDays(false);
    setSelectedImage(null);
    setAvatar(null);
  };

  const handleConfirm = () => {
    closeDialog();
    handleClose();
  };

  const onInvalid = (errors: FieldErrors<TagFormValues>) => {
    console.error("Form validation errors:", errors);

    toast({
      variant: "destructive",
      title: "Form has errors",
      description: "Please fix all validation errors before submitting.",
    });
  };

  const onSubmit = async (formData: TagFormValues) => {
    const parentPath = formData.type === "branch" ? [] : selectedParent?.path ?? [];
    if (selectedImage && formData.type !== "branch") {
      const uploadRes = await UploadCognitoImage(selectedImage);

      if (!uploadRes.success || !uploadRes.location) {
        toast({
          variant: "destructive",
          title: "Image upload failed",
          description: uploadRes.message || "Could not upload image.",
        });
        return;
      }

      formData.metadata.logo = uploadRes.location
    }

    try {
      if (action === "edit" && !tagId) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Tag id is missing for edit mode",
        });
        return;
      }

      if (action === "edit" && tagId) {
        const updatePayload: TagUpdateRequest = {
          ...formData,
          tag_id: tagId,
          path: formData.type === "branch" ? [tagId] : [...parentPath, tagId],
        };
        await updateTag(updatePayload).unwrap();
      } else {
        await createTag({ ...formData, path: [...parentPath], org_id: userOrgId }).unwrap();
      }

      toast({
        variant: "success",
        title: "Success",
        description:
          action === "edit" ? "Tag updated successfully" : "Tag created successfully",
      });
      handleClose();
      refetch();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error?.data?.message || "An error occurred while creating the business unit",
      });
    }
  };

  const parentPlacementText =
    watchedTagType === "branch"
      ? "This business unit will be placed at Organization root."
      : selectedParent
        ? `This business unit will be placed under: ${selectedParent.path.join(" / ")}`
        : "Select a parent business unit to define where this business unit will be placed.";

  const getServiceSlotByDay = (dayName: string) =>
    serviceMetadata.schedules.find((slot) => slot.day === toWeekDay(dayName));

  const isSubmitting = isLoading || isUpdating;

  const renderStaffSelector = (
    label: string,
    selectedValue: string | null | undefined,
    onSelect: (value: string | null) => void,
    openState: boolean,
    setOpenState: React.Dispatch<React.SetStateAction<boolean>>
  ) => (
    <div className="w-full flex flex-col gap-1">
      <label className="text-sm font-medium">{label}</label>
      <Popover open={openState} onOpenChange={setOpenState} modal>
        <PopoverTrigger asChild>
          <Button
            type="button"
            className="capitalize w-full scale-100 shadow-sm border-checkboxborder border-[1px] bg-white text-gray-600 hover:bg-white"
          >
            {selectedValue
              ? (staffOptions as StaffOption[]).find((staff) => staff.value === selectedValue)
                  ?.label ?? selectedValue
              : `Select ${label}`}
            <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 relative" side="bottom">
          <Command className="w-full">
            <CommandList>
              <CommandInput placeholder={`Search ${label.toLowerCase()}...`} />
            <CommandEmpty>No staff found.</CommandEmpty>
              <CommandGroup>
                {(staffOptions as StaffOption[]).map((staff) => (
                  <CommandItem
                    key={staff.value}
                    value={`${staff.label} ${staff.value}`}
                    onSelect={() => {
                      onSelect(selectedValue === staff.value ? null : staff.value);
                      setOpenState(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4 rounded-full border-2 border-green-500",
                        selectedValue === staff.value ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <div className="flex flex-col">
                      <span>{staff.label}</span>
                      <span className="text-xs text-muted-foreground">ID: {staff.value}</span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );

  return (
    <Sheet open={open} onOpenChange={(nextOpen) => !nextOpen && setIsDialogOpen(true)}>
      <SheetContent
        hideCloseButton
        className="!max-w-[1050px] p-0 custom-scrollbar sm:w-[90%] sm:max-w-2xl "
      >
        <FormProvider {...form}>
          <form
            onSubmit={handleSubmit(onSubmit, onInvalid)}
            className="flex flex-col gap-4 bg-gray-100 h-screen"
            noValidate
          >
            <SheetHeader className="sticky top-0 z-40 px-4 py-3 bg-white border-2 border-transparent border-b-gray-200">
              <SheetTitle>
                <div className="flex justify-between gap-5 items-center bg-white">
                  <p className="font-semibold">{action === "edit" ? "Edit Business Unit" : "Create Business Unit"}</p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      onClick={() => setIsDialogOpen(true)}
                      className="w-[100px] gap-2 bg-transparent border border-primary text-black hover:border-primary hover:bg-muted"
                    >
                      <i className="fa-regular fa-x text-xs font-semibold"></i>
                      Cancel
                    </Button>
                    <LoadingButton type="submit" loading={isSubmitting} disabled={isSubmitting}>
                      {!isSubmitting && (
                        <i className="fa-regular fa-floppy-disk text-base px-1"></i>
                      )}
                      {action === "edit" ? "Update" : "Save"}
                    </LoadingButton>
                  </div>
                </div>
              </SheetTitle>
            </SheetHeader>

            <SheetDescription className="p-4 mx-4 bg-white rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                <FloatingLabelInput
                  {...register("name")}
                  label="Name"
                  error={errors.name?.message}
                  type="text"
                  className={`w-full h-10 px-3 py-2 border rounded-md ${
                    errors.name ? "border-red-500" : "border-gray-300"
                  }`}
                />

                <FloatingLabelInput
                  {...register("code")}
                  label="Code"
                  error={errors.code?.message}
                  type="text"
                  className={`w-full h-10 px-3 py-2 border rounded-md ${
                    errors.code ? "border-red-500" : "border-gray-300"
                  }`}
                />

                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <div className="w-full">
                      <Select value={field.value ?? ""} onValueChange={field.onChange}>
                        <SelectTrigger
                          className={`w-full h-10 ${
                            errors.type ? "border-red-500" : "border-gray-300"
                          }`}
                          floatingLabel="Tag Type"
                        >
                          <SelectValue placeholder="Select business unit type" />
                        </SelectTrigger>
                        <SelectContent>
                          {TAG_TYPES.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type.charAt(0).toUpperCase() + type.slice(1)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="h-[20px] text-red-500 text-xs mt-[5px]">
                        {errors.type?.message || ""}
                      </div>
                    </div>
                  )}
                />

                <Controller
                  name="tag_status"
                  control={control}
                  render={({ field }) => (
                    <div className="px-3 h-10 flex items-center gap-2">
                      <Switch checked={Boolean(field.value)} onCheckedChange={field.onChange} />
                      <span className="text-sm">{watchedStatus ? "Active" : "Inactive"}</span>
                    </div>
                  )}
                />

                <Controller
                  name="parent_tag_id"
                  control={control}
                  render={({ field }) => (
                    <div className="w-full">
                      <Popover open={isParentComboboxOpen} onOpenChange={setIsParentComboboxOpen} modal>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            role="combobox"
                            aria-expanded={isParentComboboxOpen}
                            disabled={watchedTagType === "branch"}
                            className={cn(
                              "w-full h-10 justify-between font-normal",
                              errors.parent_tag_id ? "border-red-500" : "border-gray-300"
                            )}
                          >
                            {field.value && selectedParent
                              ? `${selectedParent.name} (${selectedParent.type})`
                              : watchedTagType === "branch"
                                ? "No parent needed"
                                : "Select parent"}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                          <Command>
                            <CommandInput placeholder="Search parent..." />
                            <CommandEmpty>No parent business unit found.</CommandEmpty>
                            <CommandList>
                              <CommandGroup>
                                {parentDropdownOptions.filter((option) => {
                                    // Since any path includes its tagId and the tagId of its parents
                                    // this ensures the tag itself or its children are not selectable
                                    // as its parents (circular path / dependency)
                                    if (!tagId) return true
                                    return !option.path.includes(tagId)
                                }).filter((option) => dropdownTagType?.includes(option.type)).map((option) => (
                                  <CommandItem
                                    key={option.tag_id}
                                    value={`${option.name} ${option.path.join("/")} ${option.type}`}
                                    onSelect={() => {
                                      field.onChange(option.tag_id);
                                      setIsParentComboboxOpen(false);
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4",
                                        field.value === option.tag_id
                                          ? "opacity-100"
                                          : "opacity-0"
                                      )}
                                    />
                                    <div className="flex flex-col">
                                      <span>{option.name}</span>
                                      {/* <span className="text-xs text-muted-foreground">
                                        {option.path.join(" / ")} ({option.type})
                                      </span> */}
                                    </div>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      <div className="text-xs text-muted-foreground mt-1">{parentPlacementText}</div>
                      <div className="h-[20px] text-red-500 text-xs mt-[5px]">
                        {errors.parent_tag_id?.message || ""}
                      </div>
                    </div>
                  )}
                />

                <div className="md:col-span-3 rounded-lg border border-gray-200 bg-white p-4">
                  <div className="mb-3">
                    <h3 className="text-sm font-semibold text-gray-900">Metadata</h3>
                    <p className="text-xs text-muted-foreground">
                      Backend-aligned metadata fields for {watchedTagType} tags.
                    </p>
                  </div>

                  {(watchedTagType === "department" || watchedTagType === "service") && (
                    <div className="mb-4 flex justify-start items-center">
                      <div className="flex flex-row gap-4 items-center">
                        <div className="flex justify-center items-center ">
                          <img
                            src={avatar ? String(avatar) : profileimg}
                            loading="lazy"
                            alt={String(profileimg)}
                            className="w-24 h-24 rounded-full object-cover relative border-2 border-primary"
                          />
                        </div>
                        <input
                          type="file"
                          id="tagMetadataLogoInput"
                          className="hidden"
                          onChange={handleImageChange}
                        />
                        <Button
                          onClick={handleSetAvatarClick}
                          variant={"outline"}
                          type="button"
                          className="px-4 w-60 text-nowrap py-2 bg-transparent gap-2 text-black rounded hover:bg-primary hover:text-gray-500 "
                        >
                          <Webcam className="h-4 w-4" />
                          Select Logo
                        </Button>
                        {(currentLogo || selectedImage !== null) && (
                          <Button
                            onClick={handleRemoveImageClick}
                            variant={"outline"}
                            type="button"
                            className="px-4 w-fit bg-red-500 text-nowrap py-2 bg-transparent gap-2 text-black rounded hover:bg-primary hover:text-gray-500 "
                          >
                            <i className="fa-regular fa-trash-alt text-red-600"></i>
                          </Button>
                        )}
                      </div>
                    </div>
                  )}

                  {watchedTagType === "branch" && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium">Routing Permission</label>
                        <div className="flex rounded-md overflow-hidden border border-gray-200">
                          <button
                            type="button"
                            onClick={() => setMetadataField("branch", "routing_permission", "within")}
                            className={`flex-1 py-2 px-3 text-sm min-h-[40px] text-nowrap ${
                              branchMetadata.routing_permission === "within"
                                ? "bg-primary text-white"
                                : "bg-white text-gray-800"
                            }`}
                          >
                            Within
                          </button>
                          <button
                            type="button"
                            onClick={() => setMetadataField("branch", "routing_permission", "across")}
                            className={`flex-1 py-2 px-3 text-sm min-h-[40px] text-nowrap ${
                              branchMetadata.routing_permission === "across"
                                ? "bg-primary text-white"
                                : "bg-white text-gray-800"
                            }`}
                          >
                            Across
                          </button>
                        </div>
                      </div>
                      {renderStaffSelector(
                        "Branch Manager",
                        branchMetadata.branch_manager_id,
                        (value: string | null) => setMetadataField("branch", "branch_manager_id", value),
                        isBranchManagerOpen,
                        setIsBranchManagerOpen
                      )}
                    </div>
                  )}

                  {watchedTagType === "department" && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                      <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium">Re-Routing</label>
                        <div className="flex rounded-md overflow-hidden border border-gray-200">
                          <button
                            type="button"
                            onClick={() => setMetadataField("department", "re_routing", "within")}
                            className={`flex-1 py-2 px-3 text-sm min-h-[40px] text-nowrap ${
                              departmentMetadata.re_routing === "within"
                                ? "bg-primary text-white"
                                : "bg-white text-gray-800"
                            }`}
                          >
                            Within
                          </button>
                          <button
                            type="button"
                            onClick={() => setMetadataField("department", "re_routing", "across")}
                            className={`flex-1 py-2 px-3 text-sm min-h-[40px] text-nowrap ${
                              departmentMetadata.re_routing === "across"
                                ? "bg-primary text-white"
                                : "bg-white text-gray-800"
                            }`}
                          >
                            Across
                          </button>
                        </div>
                      </div>
                      <div>
                        <FloatingLabelInput
                          label="Description"
                          value={departmentMetadata.description ?? ""}
                          onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                            setMetadataField("department", "description", event.target.value)
                          }
                          className="w-full"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Contact Number</label>
                        <PhoneInputField
                          value={departmentMetadata.contact_number ?? ""}
                          onChange={value => setMetadataField("department", "contact_number", value)}
                          placeholder="Enter contact number"
                        />
                      </div>
                      {renderStaffSelector(
                        "Coordinator",
                        departmentMetadata.coordinator_id,
                        (value) => setMetadataField("department", "coordinator_id", value),
                        isCoordinatorOpen,
                        setIsCoordinatorOpen
                      )}
                    </div>
                  )}

                  {watchedTagType === "service" && (
                    <div className="flex flex-col gap-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                        {renderStaffSelector(
                          "Lead",
                          serviceMetadata.lead_id,
                          (value) => setMetadataField("service", "lead_id", value),
                          isLeadOpen,
                          setIsLeadOpen
                        )}
                        <div>
                          <label className="text-sm font-medium">Contact Number</label>
                          <PhoneInputField
                            value={serviceMetadata.contact_number ?? ""}
                            onChange={setServiceContactNumber}
                            placeholder="Enter contact number"
                          />
                        </div>
                        {/* <FloatingLabelInput
                          label="Average Processing Time (minutes)"
                          type="number"
                          value={String(serviceMetadata.average_processing_time)}
                          onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                            setMetadataField(
                              "service", 
                              "average_processing_time",
                              Number(event.target.value || 0)
                            )
                          }
                        />
                        <FloatingLabelInput
                          label="Max Tickets (optional)"
                          type="number"
                          value={serviceMetadata.max_tickets?.toString() ?? ""}
                          onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                            setMetadataField(
                              "service",
                              "max_tickets",
                              event.target.value ? Number(event.target.value) : null
                            )
                          }
                        /> */}
                      </div>

                      <div className="flex flex-col items-start just">
                        <label className="text-sm font-medium pb-2">
                          Service Visibility
                        </label>
                        <div className="flex items-center border rounded-md overflow-hidden">
                          <button
                            type="button"
                            className={`px-4 py-2 text-sm font-medium ${
                              serviceMetadata.visibility === "visible"
                                ? "bg-primary text-white"
                                : "bg-white text-gray-700 hover:bg-gray-100"
                            }`}
                            onClick={() => setMetadataField("service", "visibility", "visible")}
                          >
                            Visible
                          </button>
                          <button
                            type="button"
                            className={`px-4 py-2 text-sm font-medium ${
                              serviceMetadata.visibility === "hidden"
                                ? "bg-primary text-white"
                                : "bg-white text-gray-700 hover:bg-gray-100"
                            }`}
                            onClick={() => setMetadataField("service", "visibility", "hidden")}
                          >
                            Hidden
                          </button>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-sm font-medium text-[#212529]">
                          Days of the Week <span className="text-red-500">*</span> :
                        </h4>
                        <div className="flex gap-2">
                          {serviceDays.map(({ id, day, title }) => (
                            <button
                              key={id}
                              type="button"
                              onClick={() => toggleDaySelection(id, day)}
                              className={`flex h-8 w-8 items-center justify-center rounded-lg text-sm font-medium ${
                                selectedDays.includes(id)
                                  ? "bg-primary text-white"
                                  : "bg-[#FBF6D5] text-primary"
                              }`}
                            >
                              {title}
                            </button>
                          ))}
                        </div>
                        <div className="flex items-center gap-2 ml-2">
                          <Switch
                            id="apply-time-to-all-days"
                            checked={applyToAllDays}
                            onCheckedChange={(checked) => {
                              setApplyToAllDays(checked);
                              setMetadataField("service", "apply_time_to_all_days", checked);
                            }}
                          />
                          <span className="text-xs text-muted-foreground">
                            Apply time to all days
                          </span>
                        </div>
                      </div>

                      <section className="space-y-3">
                        {selectedDays.map((id) => {
                          const selectedDay = serviceDays.find((day) => day.id === id)?.day;
                          if (!selectedDay) return null;
                          const slot = getServiceSlotByDay(selectedDay);

                          return (
                            <div key={id} className="flex gap-4 items-end">
                              <h3 className="text-[.9rem] font-medium text-[#212529] py-3 w-full max-w-24 text-right">
                                {selectedDay}
                              </h3>
                              <div className="flex gap-4 flex-wrap items-center">
                                <TimeInput
                                  label={"Opening Time"}
                                  value={slot?.opening_time ?? ""}
                                  onChange={(event) =>
                                    handleServiceScheduleTimeChange(
                                      selectedDay,
                                      "opening_time",
                                      event.target.value
                                    )
                                  }
                                  className="input"
                                  placeholder="Start Time"
                                />
                                {/*}
                                <TimeInput
                                  label={"Break Start Time"}
                                  value={slot?.break_start_time ?? ""}
                                  onChange={(event) =>
                                    handleServiceScheduleTimeChange(
                                      selectedDay,
                                      "break_start_time",
                                      event.target.value
                                    )
                                  }
                                  className="input"
                                  placeholder="Break Start"
                                />
                                <TimeInput
                                  label={"Break End Time"}
                                  value={slot?.break_end_time ?? ""}
                                  onChange={(event) =>
                                    handleServiceScheduleTimeChange(
                                      selectedDay,
                                      "break_end_time",
                                      event.target.value
                                    )
                                  }
                                  className="input"
                                  placeholder="Break End"
                                /> */}
                                <TimeInput
                                  label={"Ticket End Time"}
                                  value={slot?.ticket_end_time ?? ""}
                                  onChange={(event) =>
                                    handleServiceScheduleTimeChange(
                                      selectedDay,
                                      "ticket_end_time",
                                      event.target.value
                                    )
                                  }
                                  className="input"
                                  placeholder="Ticket End"
                                />
                                <TimeInput
                                  label={"Closing Time"}
                                  value={slot?.closing_time ?? ""}
                                  onChange={(event) =>
                                    handleServiceScheduleTimeChange(
                                      selectedDay,
                                      "closing_time",
                                      event.target.value
                                    )
                                  }
                                  className="input"
                                  placeholder="Closing Time"
                                />
                              </div>
                            </div>
                          );
                        })}
                      </section>

                      <div className="w-full pt-2">
                        <Select
                          value={serviceMetadata.form_id ? String(serviceMetadata.form_id) : "none"}
                          onValueChange={(val) =>
                            setMetadataField(
                              "service",
                              "form_id",
                              val === "none" ? null : Number(val)
                            )
                          }
                        >
                          <SelectTrigger
                            className="w-full h-10 border-gray-300"
                            floatingLabel="Appointment Form"
                          >
                            <SelectValue placeholder="Select Form (optional)" />
                          </SelectTrigger>
                          <SelectContent className="capitalize max-h-56 custom-scrollbar">
                            <SelectItem value="none">-- None --</SelectItem>
                            {formOptions.map((item) => (
                              <SelectItem key={item.value} value={String(item.value)}>
                                {item.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                        <div className="flex items-start flex-col gap-2 w-full pb-2 pt-2 ">
                          <Controller
                            name="metadata.tags"
                            control={control}
                            defaultValue={[]}
                            rules={{
                              validate: {
                                maxTags: (value) =>
                                  (value ?? []).length <= 10 || "Maximum 10 tags allowed",
                                uniqueTags: (value) =>
                                  new Set(value).size === (value ?? []).length ||
                                  "Duplicate tags are not allowed",
                              },
                            }}
                            render={({ field, fieldState }) => (
                                <>
                              <InputTags
                                value={field.value || []}
                                onChange={(newTags) => {
                                  field.onChange(newTags);
                                }}
                              />
                              {fieldState.error && (
                                <span className="text-destructive font-poppins block !mt-[5px] text-xs">
                                  {fieldState.error.message}
                                </span>
                              )}
                              </>
                            )}
                          />
                        </div>
                    </div>
                  )}
                </div>
              </div>
            </SheetDescription>
            <ConfirmationDialog
              isOpen={isDialogOpen}
              onClose={closeDialog}
              onConfirm={handleConfirm}
            />
          </form>
        </FormProvider>
      </SheetContent>
    </Sheet>
  );
};

export default TagsForm;
